import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";

export interface AmplifyConfigOptions {
  userPoolId: string;
  userPoolClientId: string;
  apiEndpoint: string;
  awsRegion?: string;
  apiName?: string;
  /**
   * Cookie domain to use for cross-subdomain session sharing. If omitted,
   * `resolveCookieDomain` is called with `cookieDomainSuffixes` to pick the
   * right domain based on `window.location.hostname`.
   */
  cookieDomain?: string;
  /**
   * Suffix → resolved-domain rules used by `resolveCookieDomain` when no
   * explicit `cookieDomain` is provided. Apps inject their environment-specific
   * suffix list (e.g. `.dev.acme.com` → `.dev.acme.com`).
   */
  cookieDomainSuffixes?: Array<{ suffix: string; domain: string }>;
}

const PSL_DOMAIN_SUFFIXES = [".amplifyapp.com"];

/**
 * Resolves the cookie domain based on the current hostname.
 *
 * - `localhost` / `127.0.0.1` → `"localhost"`.
 * - Any hostname ending in a Public Suffix List entry (e.g. `*.amplifyapp.com`)
 *   → the full hostname (browsers reject cookies set on PSL domains).
 * - Otherwise: longest matching suffix from `suffixes` wins. Apps inject
 *   their own suffix list — the `frontend-auth` package stays
 *   tenant-/brand-agnostic.
 * - Final fallback: `fallbackDomain ?? hostname ?? "localhost"`.
 */
export function resolveCookieDomain(
  suffixes: Array<{ suffix: string; domain: string }> = [],
  fallbackDomain?: string,
): string {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "localhost";
  }

  for (const psl of PSL_DOMAIN_SUFFIXES) {
    if (hostname.endsWith(psl)) {
      return hostname;
    }
  }

  // Most-specific suffix first (e.g. `.dev.acme.com` matches before `.acme.com`).
  const sorted = [...suffixes].sort((a, b) => b.suffix.length - a.suffix.length);
  for (const { suffix, domain } of sorted) {
    if (hostname.endsWith(suffix)) {
      return domain;
    }
  }

  return fallbackDomain ?? hostname ?? "localhost";
}

/**
 * Configures AWS Amplify with Cognito authentication and an optional REST API.
 * Sets up cookie storage so a session created on one subdomain is reused on
 * sibling subdomains.
 */
export function configureAmplify(options: AmplifyConfigOptions): void {
  const {
    userPoolId,
    userPoolClientId,
    apiEndpoint,
    awsRegion = "us-east-1",
    apiName = "cakewalk-api",
    cookieDomain: providedCookieDomain,
    cookieDomainSuffixes,
  } = options;

  if (!userPoolId || !userPoolClientId || !apiEndpoint) {
    throw new Error("configureAmplify: userPoolId, userPoolClientId, and apiEndpoint are required");
  }

  const cookieDomain = providedCookieDomain ?? resolveCookieDomain(cookieDomainSuffixes);

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
      },
    },
    API: {
      REST: {
        [apiName]: {
          endpoint: apiEndpoint,
          region: awsRegion,
        },
      },
    },
  });

  // 30-day cookie matches Cognito's default refresh-token lifetime — longer
  // values leave orphan cookies after the refresh token expires.
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: cookieDomain,
      path: "/",
      expires: 30,
      sameSite: "lax",
      secure: cookieDomain !== "localhost",
    }),
  );
}
